import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser, LoginResponse, Role } from './types';
import {
  clearTokens,
  getAccessToken,
  getStoredUserJson,
  setAccessToken,
  setRefreshToken,
  setStoredUserJson,
} from './tokenStorage';
import { login as loginApi } from '@/features/auth/api';
import { roleHomePath } from '@/core/permissions';

interface AuthContextValue {
  user: AuthUser | null;
  role: Role | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<LoginResponse>;
  loginAsDevUser: (user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  logout: () => void;
  homePath: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUser(): AuthUser | null {
  const raw = getStoredUserJson();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    const stored = readStoredUser();
    if (stored) {
      setUser(stored);
      // Access token is memory-only; in DEV re-hydrate a session token so refresh
      // keeps the shell usable. Production will rely on silent refresh cookie/token.
      if (import.meta.env.DEV && !getAccessToken()) {
        setAccessToken(`dev.restored.${stored.id}.${Date.now()}`);
      }
    }
    setIsBootstrapping(false);

    const onExpired = () => {
      clearTokens();
      setUser(null);
    };
    window.addEventListener('rcats:auth-expired', onExpired);
    return () => window.removeEventListener('rcats:auth-expired', onExpired);
  }, []);

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    const result = await loginApi(email, password, mfaCode);
    setAccessToken(result.accessToken);
    setRefreshToken(result.refreshToken);
    setStoredUserJson(JSON.stringify(result.user));
    setUser(result.user);
    return result;
  }, []);

  const loginAsDevUser = useCallback((devUser: AuthUser) => {
    const fakeToken = `dev.${btoa(devUser.email)}.${Date.now()}`;
    setAccessToken(fakeToken);
    setRefreshToken(`refresh.${fakeToken}`);
    setStoredUserJson(JSON.stringify(devUser));
    setUser(devUser);
  }, []);

  const persistUser = useCallback((next: AuthUser) => {
    setStoredUserJson(JSON.stringify(next));
    setUser(next);
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: user?.role ?? null,
      isAuthenticated: Boolean(user && getAccessToken()),
      isBootstrapping,
      login,
      loginAsDevUser,
      setUser: persistUser,
      logout,
      homePath: roleHomePath(user?.role),
    }),
    [user, isBootstrapping, login, loginAsDevUser, persistUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
